import { useContext } from 'react'
import { OrganizationContext } from '@/contexts/OrganizationContext'
import { PRODUCT_KEYS } from '@/config/studioProducts'
import { ROLE_HIERARCHY, ROLES } from '@/constants/roles'

/**
 * Why a Prepacks picture control can't upload or choose from the media library, or null where it can. Both live
 * in the Feed media library, so the API admits Feed access for the library and Feed publishing (with the editor
 * role) for an upload. Since #884 it also admits Prepacks view and configuration access to both, which is what
 * saving a template, pack or schedule needs, so a Prepacks-only organization can upload the pictures it saves. The
 * conditions mirror mediaLibraryBrowseGrants and mediaLibraryUploadGrants exactly, so a per-user override that
 * grants one action without the other is withheld here as the API would withhold it. Generating a picture and
 * typing its URL need neither. Without an organization context nothing is known, so nothing is withheld; the API
 * still decides.
 */
export function usePictureMediaAccess() {
  const organization = useContext(OrganizationContext)
  if (!organization?.hasProductAccess) return { uploadUnavailable: null, libraryUnavailable: null }
  const feed = organization.hasProductAccess(PRODUCT_KEYS.FEED, 'view')
  const editor = (ROLE_HIERARCHY[organization.currentOrg?.role] || 0) >= ROLE_HIERARCHY[ROLES.EDITOR]
  const publish = editor && feed && organization.hasProductAccess(PRODUCT_KEYS.FEED, 'publish')
  const prepacks = organization.hasProductAccess(PRODUCT_KEYS.PREPACKS, 'view') && organization.hasProductAccess(PRODUCT_KEYS.PREPACKS, 'configure')
  return {
    uploadUnavailable: publish || prepacks ? null : 'Uploading needs Feed publishing access or Prepacks configuration access.',
    libraryUnavailable: feed || prepacks ? null : 'Choosing from the library needs Feed access or Prepacks configuration access.',
  }
}
